import React, { useState, useRef, useEffect } from 'react';
import { 
  Settings, 
  Eye, 
  EyeOff, 
  GripVertical, 
  RotateCcw, 
  Download, 
  Upload,
  X,
  ChevronDown,
  ChevronUp,
  Monitor,
  Smartphone,
  Tablet,
  Lightbulb,
  Save,
  AlertCircle
} from 'lucide-react';
import dashboardCustomizationService from '../../services/dashboardCustomizationService';
import { SelectField } from "components/ui/select-field";

const DashboardCustomizer = ({ config, onConfigChange, onClose }) => {
  const [activeTab, setActiveTab] = useState('layout');
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [dragOverWidget, setDragOverWidget] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [expandedWidget, setExpandedWidget] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Load behavior recommendations on mount
  useEffect(() => {
    if (config?.userId) {
      const recs = dashboardCustomizationService.getBehaviorRecommendations(config.userId, config);
      setRecommendations(recs);
    }
  }, [config]);

  // Handle drag start
  const handleDragStart = (e, widget) => {
    setDraggedWidget(widget);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  // Handle drag over
  const handleDragOver = (e, widget) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverWidget(widget);
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverWidget(null);
  };

  // Handle drop
  const handleDrop = async (e, targetWidget) => {
    e.preventDefault();
    setDragOverWidget(null);
    
    if (!draggedWidget || !targetWidget || draggedWidget.id === targetWidget.id) {
      setDraggedWidget(null);
      return;
    }

    // Reorder widgets
    const widgets = [...config.layout.widgets];
    const draggedIndex = widgets.findIndex(w => w.id === draggedWidget.id);
    const targetIndex = widgets.findIndex(w => w.id === targetWidget.id);
    
    // Remove dragged widget and insert at target position
    const [removed] = widgets.splice(draggedIndex, 1);
    widgets.splice(targetIndex, 0, removed);
    
    // Update orders
    const updatedWidgets = widgets.map((widget, index) => ({
      ...widget,
      order: index + 1
    }));

    const updatedConfig = {
      ...config,
      layout: {
        ...config.layout,
        widgets: updatedWidgets
      }
    };

    onConfigChange(updatedConfig);
    await dashboardCustomizationService.saveConfiguration(updatedConfig);
    setDraggedWidget(null);
  };

  // Toggle widget visibility
  const toggleWidgetVisibility = async (widgetId) => {
    const updatedConfig = await dashboardCustomizationService.toggleWidgetVisibility(config, widgetId);
    onConfigChange(updatedConfig);
  };

  // Update widget size
  const updateWidgetSize = async (widgetId, newSize) => {
    const updatedConfig = await dashboardCustomizationService.updateWidgetSize(config, widgetId, newSize);
    onConfigChange(updatedConfig);
  };

  // Update widget configuration
  const updateWidgetConfig = async (widgetId, widgetConfig) => {
    const updatedConfig = await dashboardCustomizationService.updateWidgetConfig(config, widgetId, widgetConfig);
    onConfigChange(updatedConfig);
  };

  // Reset to defaults
  const handleResetToDefaults = async () => {
    if (!config?.userId || !config?.role) return;
    
    const defaultConfig = await dashboardCustomizationService.resetToDefaults(config.userId, config.role);
    onConfigChange(defaultConfig);
    setShowResetConfirm(false);
  };

  // Export configuration
  const handleExport = () => {
    dashboardCustomizationService.exportConfiguration(config);
  };

  // Import configuration
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const importedConfig = await dashboardCustomizationService.importConfiguration(file, config.userId);
      onConfigChange(importedConfig);
    } catch (error) {
      alert('Failed to import configuration: ' + error.message);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Apply recommendation
  const applyRecommendation = async (recommendation) => {
    switch (recommendation.type) {
      case 'enable_widget':
        await toggleWidgetVisibility(recommendation.widgetId);
        break;
      case 'reorder_widget':
        // Implementation for reordering based on recommendation
        break;
      default:
        break;
    }
    
    // Refresh recommendations
    const newRecs = dashboardCustomizationService.getBehaviorRecommendations(config.userId, config);
    setRecommendations(newRecs);
  };

  // Save configuration
  const handleSave = async () => {
    setSaving(true);
    try {
      await dashboardCustomizationService.saveConfiguration(config);
      // Show success feedback
      setTimeout(() => setSaving(false), 1000);
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setSaving(false);
    }
  };

  // Get widget title
  const getWidgetTitle = (type) => {
    const titles = {
      kpi: 'Key Performance Indicators',
      attention: 'Needs Attention',
      performance: 'Performance Trends',
      topPerformers: 'Top Performers',
      quickActions: 'Quick Actions',
      teamActivity: 'Team Activity'
    };
    return titles[type] || type;
  };

  // Get size options for widget
  const getSizeOptions = (widgetType) => {
    const options = [
      { value: 'quarter', label: '1/4 Width', icon: '▌' },
      { value: 'third', label: '1/3 Width', icon: '▌▌' },
      { value: 'half', label: '1/2 Width', icon: '▌▌▌' },
      { value: 'full', label: 'Full Width', icon: '▌▌▌▌' }
    ];

    // Some widgets work better at certain sizes
    if (widgetType === 'kpi') {
      return options.filter(opt => ['half', 'full'].includes(opt.value));
    }
    
    return options;
  };

  // Render widget configuration panel
  const renderWidgetConfig = (widget) => {
    const isExpanded = expandedWidget === widget.id;
    
    return (
      <div key={widget.id} className="border border-line rounded-lg mb-3">
        <div 
          className={`p-3 cursor-pointer transition-colors ${
            dragOverWidget?.id === widget.id ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-800/50' : 'hover:bg-gray-50 dark:hover:bg-dark-700'
          }`}
          draggable
          onDragStart={(e) => handleDragStart(e, widget)}
          onDragOver={(e) => handleDragOver(e, widget)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, widget)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <GripVertical className="w-4 h-4 text-content-subtle cursor-grab" />
              <div>
                <h4 className="font-medium text-content">{getWidgetTitle(widget.type)}</h4>
                <p className="text-sm text-content-muted">Order: {widget.order}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => toggleWidgetVisibility(widget.id)}
                className={`p-1 rounded transition-colors ${
                  widget.enabled 
                    ? 'text-green-600 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30' 
                    : 'text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30'
                }`}
              >
                {widget.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              
              <button
                onClick={() => setExpandedWidget(isExpanded ? null : widget.id)}
                className="p-1 text-content-subtle hover:text-content-secondary transition-colors"
              >
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4 border-t border-line bg-gray-50 dark:bg-dark-700">
            {/* Size Configuration */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Widget Size
              </label>
              <div className="grid grid-cols-2 gap-2">
                {getSizeOptions(widget.type).map(option => (
                  <button
                    key={option.value}
                    onClick={() => updateWidgetSize(widget.id, option.value)}
                    className={`p-2 text-sm rounded border transition-colors ${
                      widget.size === option.value
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-800/50 text-blue-700'
                        : ' border-line-strong text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
                    }`}
                  >
                    <span className="mr-2">{option.icon}</span>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Widget-specific Configuration */}
            {renderWidgetSpecificConfig(widget)}
          </div>
        )}
      </div>
    );
  };

  // Render widget-specific configuration options
  const renderWidgetSpecificConfig = (widget) => {
    switch (widget.type) {
      case 'performance':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Time Range
              </label>
              <SelectField
                value={widget.config?.defaultTimeRange || '30d'}
                onChange={(e) => updateWidgetConfig(widget.id, { defaultTimeRange: e.target.value })}
                className="w-full p-2 border border-line-strong rounded text-sm"
              >
                <option value="7d">7 Days</option>
                <option value="30d">30 Days</option>
                <option value="90d">90 Days</option>
              </SelectField>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Metrics
              </label>
              <div className="space-y-1">
                {['openRates', 'clickRates', 'conversionRates', 'revenue'].map(metric => (
                  <label key={metric} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={widget.config?.defaultMetrics?.includes(metric) || false}
                      onChange={(e) => {
                        const currentMetrics = widget.config?.defaultMetrics || [];
                        const newMetrics = e.target.checked
                          ? [...currentMetrics, metric]
                          : currentMetrics.filter(m => m !== metric);
                        updateWidgetConfig(widget.id, { defaultMetrics: newMetrics });
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm capitalize">{metric.replace(/([A-Z])/g, ' $1')}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );
        
      case 'topPerformers':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Tab
            </label>
            <SelectField
              value={widget.config?.defaultTab || 'campaigns'}
              onChange={(e) => updateWidgetConfig(widget.id, { defaultTab: e.target.value })}
              className="w-full p-2 border border-line-strong rounded text-sm"
            >
              <option value="campaigns">Campaigns</option>
              <option value="segments">Segments</option>
            </SelectField>
          </div>
        );
        
      case 'teamActivity':
        return (
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={widget.config?.showNotifications !== false}
                onChange={(e) => updateWidgetConfig(widget.id, { showNotifications: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Show Notifications</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={widget.config?.showTeamMetrics || false}
                onChange={(e) => updateWidgetConfig(widget.id, { showTeamMetrics: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Show Team Metrics</span>
            </label>
          </div>
        );
        
      default:
        return (
          <p className="text-sm text-content-muted">
            No specific configuration options available for this widget.
          </p>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="customizer-title">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-4xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-line">
          <div className="flex items-center space-x-3">
            <Settings className="w-6 h-6 text-blue-600 dark:text-blue-300" />
            <div>
              <h2 className="text-xl font-semibold text-content">Dashboard Customization</h2>
              <p className="text-sm text-content-secondary">Personalize your dashboard layout and preferences</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save'}</span>
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-content-subtle hover:text-content-secondary transition-colors"
              aria-label="Close customizer"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-line">
          {[
            { id: 'layout', label: 'Layout', icon: Monitor },
            { id: 'preferences', label: 'Preferences', icon: Settings },
            { id: 'recommendations', label: 'Smart Suggestions', icon: Lightbulb }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-600 dark:text-blue-300 border-b-2 border-blue-600'
                  : 'text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {tab.id === 'recommendations' && recommendations.length > 0 && (
                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-xs px-2 py-1 rounded-full">
                  {recommendations.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'layout' && (
            <div className="h-full flex">
              {/* Widget List */}
              <div className="w-1/2 p-6 border-r border-line overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-content">Widgets</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'text-content-subtle'}`}
                    >
                      <Monitor className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPreviewMode('tablet')}
                      className={`p-2 rounded ${previewMode === 'tablet' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'text-content-subtle'}`}
                    >
                      <Tablet className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'text-content-subtle'}`}
                    >
                      <Smartphone className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-content-secondary mb-4">
                  Drag widgets to reorder them. Click the eye icon to show/hide widgets.
                </p>
                
                <div className="space-y-2">
                  {config?.layout?.widgets
                    ?.sort((a, b) => a.order - b.order)
                    ?.map(widget => renderWidgetConfig(widget))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="w-1/2 p-6 overflow-y-auto">
                <h3 className="text-lg font-medium text-content mb-4">Actions</h3>
                
                <div className="space-y-4">
                  {/* Reset to Defaults */}
                  <div className="p-4 border border-line rounded-lg">
                    <h4 className="font-medium text-content mb-2">Reset Layout</h4>
                    <p className="text-sm text-content-secondary mb-3">
                      Restore the default layout for your role ({config?.role}).
                    </p>
                    
                    {showResetConfirm ? (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleResetToDefaults}
                          className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                        >
                          Confirm Reset
                        </button>
                        <button
                          onClick={() => setShowResetConfirm(false)}
                          className="px-3 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-400 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowResetConfirm(true)}
                        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Reset to Defaults</span>
                      </button>
                    )}
                  </div>
                  
                  {/* Export/Import */}
                  <div className="p-4 border border-line rounded-lg">
                    <h4 className="font-medium text-content mb-2">Backup & Restore</h4>
                    <p className="text-sm text-content-secondary mb-3">
                      Export your configuration or import a saved layout.
                    </p>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={handleExport}
                        className="flex items-center space-x-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded hover:bg-blue-200 dark:hover:bg-blue-900/40 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                      </button>
                      
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center space-x-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm rounded hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Import</span>
                      </button>
                    </div>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      onChange={handleImport}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'preferences' && (
            <div className="p-6 overflow-y-auto">
              <h3 className="text-lg font-medium text-content mb-4">Dashboard Preferences</h3>
              
              <div className="space-y-6 max-w-2xl">
                {/* General Preferences */}
                <div>
                  <h4 className="font-medium text-content mb-3">General</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Default Time Range
                      </label>
                      <SelectField
                        value={config?.preferences?.defaultTimeRange || '30d'}
                        onChange={(e) => {
                          const updatedConfig = {
                            ...config,
                            preferences: {
                              ...config.preferences,
                              defaultTimeRange: e.target.value
                            }
                          };
                          onConfigChange(updatedConfig);
                        }}
                        className="w-full p-2 border border-line-strong rounded"
                      >
                        <option value="7d">7 Days</option>
                        <option value="30d">30 Days</option>
                        <option value="90d">90 Days</option>
                      </SelectField>
                    </div>
                    
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={config?.preferences?.autoRefresh !== false}
                          onChange={(e) => {
                            const updatedConfig = {
                              ...config,
                              preferences: {
                                ...config.preferences,
                                autoRefresh: e.target.checked
                              }
                            };
                            onConfigChange(updatedConfig);
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">Auto-refresh data</span>
                      </label>
                    </div>
                    
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={config?.preferences?.compactMode || false}
                          onChange={(e) => {
                            const updatedConfig = {
                              ...config,
                              preferences: {
                                ...config.preferences,
                                compactMode: e.target.checked
                              }
                            };
                            onConfigChange(updatedConfig);
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">Compact mode (smaller widgets)</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Refresh Interval */}
                <div>
                  <h4 className="font-medium text-content mb-3">Data Refresh</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Refresh Interval
                    </label>
                    <SelectField
                      value={config?.preferences?.refreshInterval || 300000}
                      onChange={(e) => {
                        const updatedConfig = {
                          ...config,
                          preferences: {
                            ...config.preferences,
                            refreshInterval: parseInt(e.target.value)
                          }
                        };
                        onConfigChange(updatedConfig);
                      }}
                      className="w-full p-2 border border-line-strong rounded"
                      disabled={!config?.preferences?.autoRefresh}
                    >
                      <option value="60000">1 minute</option>
                      <option value="300000">5 minutes</option>
                      <option value="600000">10 minutes</option>
                      <option value="1800000">30 minutes</option>
                    </SelectField>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'recommendations' && (
            <div className="p-6 overflow-y-auto">
              <h3 className="text-lg font-medium text-content mb-4">Smart Suggestions</h3>
              
              {recommendations.length === 0 ? (
                <div className="text-center py-8">
                  <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-content mb-2">No suggestions yet</h4>
                  <p className="text-content-secondary">
                    Use your dashboard for a while and we'll provide personalized recommendations.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations.map((rec, index) => (
                    <div key={index} className="p-4 border border-line rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-full ${
                            rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300' :
                            rec.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300' :
                            'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
                          }`}>
                            <AlertCircle className="w-4 h-4" />
                          </div>
                          
                          <div>
                            <h4 className="font-medium text-content mb-1">
                              {rec.type === 'enable_widget' ? 'Enable Widget' : 'Reorder Widget'}
                            </h4>
                            <p className="text-sm text-content-secondary">{rec.reason}</p>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => applyRecommendation(rec)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardCustomizer;
